'use client';
import { Anchor } from '@repo/ui/src/components/Anchor';
import { MetaProps } from '@repo/ui/src/types';
import { classNames } from '@repo/ui/src/utils/classNames';
import Image from '@repo/ui/src/components/Image/New';
import { navigationClicked } from '@repo/ui/src/services/analytics';
import buildImageSrc from '@repo/ui/src/functions/image-functions-aspect-ratio';
import { appConfig } from '../../utils/appConfig';

export interface LinkWithIconProps {
  _meta: MetaProps;
  displayName: string;
  externalLink: string;
  imageContent: any;
  isExternal: boolean;
  link: string;
  noFollow: boolean;
  noIndex: boolean;
  anchorTag?: string;
}

export interface LinkGroupProps {
  _meta: MetaProps;
  links: Array<LinkWithIconProps>;
  title?: string;
  primaryHeader?: string;
  secondaryHeader?: string;
}

export const LinkGroup = ({ links, primaryHeader, secondaryHeader }: LinkGroupProps) => {
  if (links?.length === 0) return null;
  const hasTwo = links?.length === 2;

  return (
    <ul
      className={classNames(
        'tw-grid tw-px-6 tw-gap-y-6 tw-gap-x-6 md:tw-pl-0 tw-list-none',
        hasTwo ? 'sm:tw-grid-cols-[minmax(0,2fr)_minmax(0,1fr)]' : 'sm:tw-grid-cols-3',
      )}
    >
      {links?.map(
        (
          {
            link,
            imageContent,
            displayName,
            isExternal,
            noFollow,
            noIndex,
            externalLink,
            anchorTag,
          },
          i,
        ) => (
          <li key={i}>
            <Anchor
              className="tw-grid tw-grid-cols-2 tw-gap-x-4 sm:tw-flex tw-flex-col tw-group/link-hover"
              link={link}
              isExternal={isExternal}
              externalLink={externalLink}
              noFollow={noFollow}
              noIndex={noIndex}
              anchorTag={anchorTag}
              onClick={() =>
                navigationClicked({
                  nav_type: 'Top Navigation',
                  clicked_text: displayName ?? '',
                  primary_header: primaryHeader ?? '',
                  secondary_header: secondaryHeader ?? '',
                  tertiary_header: displayName ?? '',
                })
              }
            >
              <div className="tw-block tw-h-auto tw-gap-x-4 tw-cursor-pointer tw-aspect-video sm:tw-aspect-square tw-max-h-48">
                {imageContent && (
                  <div
                    className={classNames(
                      'tw-relative tw-h-full tw-overflow-hidden',
                      appConfig.isTraeger && 'tw-rounded',
                    )}
                  >
                    <Image
                      src={buildImageSrc(imageContent?.image, 300)}
                      alt={imageContent?.image?.accessibilityLabel}
                      className={classNames(
                        'tw-h-full sm:tw-w-auto tw-object-cover tw-object-center group-hover/link-hover:tw-scale-150 tw-transition-all group-hover/link-hover:tw-duration-[5000ms] group-hover/link-hover:tw-ease-in tw-ease-out tw-duration-1000',
                        hasTwo ? 'tw-w-full tw-h-[240px]' : 'tw-aspect-square',
                      )}
                      fill
                    />
                  </div>
                )}
              </div>
              <p
                className={classNames(
                  'tw-text-neutral-700 tw-self-center md:tw-self-start group-hover/link-hover:tw-text-brand-400 group-hover/link-hover:tw-underline tw-transition-all tw-duration-300',
                  appConfig.isMeater && 'tra-text-web-heading-4 tw-pt-5 !tw-normal-case',
                  appConfig.isTraeger && 'tra-text-web-sub-3 tw-pt-2',
                )}
              >
                {displayName}
              </p>
            </Anchor>
          </li>
        ),
      )}
    </ul>
  );
};

export default LinkGroup;
